import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Card,
    CardBody,
    Button,
    Spinner,
    Alert,
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import { Product } from "./Interfaces";

const Categories = () => {
    const { user, changeProduct, setLoading, setBrands, setSelectedBrands, selectedBrands } = useAuth();
    const [allCategories, setAllCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
    const [subCategoryProducts, setSubCategoryProducts] = useState<Product[]>([]);
    const [loading, setLoadingLocal] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchCategories = async () => {
            setLoadingLocal(true);
            try {
                const response = await axios.post(
                    "https://api.apapiecesauto.fr/apiV2/",
                    {
                        action: "localcategories",
                        entity: user.entity
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${user.token}`
                        }
                    }
                );

                const categories = response.data;
                setAllCategories(categories);
            } catch (err: any) {
                setError("Erreur lors du chargement des catégories.");
            } finally {
                setLoadingLocal(false);
            }
        };

        fetchCategories();
    }, [user.token]);

    const fetchSubCategoryProducts = async (subCategoryId: string) => {
        setLoading(true);
        setBrands({});
        setSelectedBrands([]);
        try {
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "catproducts",
                    entity: user.entity,
                    id: subCategoryId
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            // 🔥 IMPORTANT : on conserve TOUTES les propriétés brutes de l'API
            const products = response.all
                .filter((item: any) => item.element === "product")
                .map((productData: any) => ({
                    ...productData, // ← garde stock, fournisseurs, consigne, monstock, etc.
                    id: productData.id,
                    came_from: "catproduct",
                    ref: productData.ref,
                    label: productData.label,
                    description: productData.description,
                    price: parseFloat(productData.price?.replace(',', '.') || '0'),
                    price_ttc: parseFloat(productData.price_ttc?.replace(',', '.') || '0'),
                    stock_reel: productData.mystock,
                    // On s'assure que la propriété 'stock' existe pour le composant Vendeur
                    stock: productData.stock || productData.monstock,
                    EPRimages: productData.EPRimages,
                    EPRimagesPath: productData.EPRimagesPath,
                    crm: {
                        label: productData.label,
                        description: productData.description,
                        id: productData.id,
                        ref: productData.ref,
                        price: parseFloat(productData.price?.replace(',', '.') || '0'),
                        price_ttc: parseFloat(productData.price_ttc?.replace(',', '.') || '0')
                    },
                    tecdoc: {
                        brandName: productData.brandName || productData.brandname || "N/A",
                        articleNo: productData.ref,
                        details: productData.tecdoc?.details || []
                    }
                }));

            setSubCategoryProducts(products);
            changeProduct(products);
            setBrands(response.brands || {});
        } catch (err: any) {
            setError("Erreur lors du chargement des produits.");
        } finally {
            setLoading(false);
            setLoadingLocal(false);
        }
    };

    const getParentCategories = () => {
        return allCategories.filter((cat: any) => cat.fk_parent === "0");
    };
    const getSubCategories = (categoryId: string) => {
        return allCategories.filter((cat: any) => cat.fk_parent === categoryId);
    };

    const handleCategoryClick = (categoryId: string) => {
        setSelectedCategory(categoryId);
        setSelectedSubCategory(null);
        const subCategories = getSubCategories(categoryId);
        if (subCategories.length === 0) {
            fetchSubCategoryProducts(categoryId);
        }
    };

    const handleSubCategoryClick = (subCategoryId: string) => {
        setSelectedSubCategory(subCategoryId);
        fetchSubCategoryProducts(subCategoryId);
    };

    return (
        <React.Fragment>
            {/* Catégories parentes */}
            <Card className="mb-4 bg-success text-white">
                <CardBody className="py-2">
                    <div className="d-flex flex-wrap justify-content-center">
                        {loading ? (
                            <Spinner color="light" />
                        ) : error ? (
                            <Alert color="danger">{error}</Alert>
                        ) : (
                            getParentCategories().map((cat: any) => (
                                <Button
                                    key={cat.rowid}
                                    color="light"
                                    outline
                                    className={`me-2 mb-2 d-flex align-items-center ${selectedCategory === cat.rowid ? "active border" : ""}`}
                                    onClick={() => handleCategoryClick(cat.rowid)}
                                >
                                    {cat.label}
                                </Button>
                            ))
                        )}
                    </div>
                </CardBody>
            </Card>

            {/* Sous-catégories */}
            {selectedCategory && getSubCategories(selectedCategory).length > 0 && (
                <Card className="mb-3 border border-light bg-white shadow-lg">
                    <CardBody className="py-2">
                        <div className="d-flex flex-wrap justify-content-center">
                            {getSubCategories(selectedCategory).map((sub: any) => (
                                <Button
                                    key={sub.rowid}
                                    color="primary"
                                    outline
                                    className={`me-2 mb-2 ${selectedSubCategory === sub.rowid ? "active border" : ""}`}
                                    onClick={() => handleSubCategoryClick(sub.rowid)}
                                >
                                    {sub.label}
                                </Button>
                            ))}
                        </div>
                    </CardBody>
                </Card>
            )}
        </React.Fragment>
    );
};

export default Categories;